import { useEffect, useRef } from 'react';
import { FinancialPlanData } from '../types/financialPlan';
import {
  getFinancialPlanShareForOwner,
  syncFinancialPlanShare,
} from '../utils/financialPlanShareService';

const SYNC_DEBOUNCE_MS = 1500;

/**
 * Pushes plan changes to an active share link (debounced).
 * No-op when no share exists for this plan.
 */
export function useFinancialPlanShareLiveSync(
  planId: string | null,
  planName: string,
  planData: FinancialPlanData,
  enabled: boolean,
): void {
  const hasShareRef = useRef<boolean | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!enabled || !planId) {
      hasShareRef.current = null;
      return;
    }
    let cancelled = false;
    void getFinancialPlanShareForOwner(planId).then((share) => {
      if (!cancelled) {
        hasShareRef.current = share?.enabled === true;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, planId]);

  useEffect(() => {
    if (!enabled || !planId) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      void (async () => {
        if (syncingRef.current) return;
        try {
          if (hasShareRef.current === false) return;
          if (hasShareRef.current === null) {
            const share = await getFinancialPlanShareForOwner(planId);
            hasShareRef.current = share?.enabled === true;
            if (!hasShareRef.current) return;
          }
          syncingRef.current = true;
          await syncFinancialPlanShare({ planId, planName, planData });
        } catch (err) {
          console.warn('syncFinancialPlanShare failed', err);
        } finally {
          syncingRef.current = false;
        }
      })();
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, planId, planName, planData]);
}
